"use client";

import { motion } from "framer-motion";
import { FileCode2, CircleCheckBig, Layers } from "lucide-react";
import { getViewModeChrome } from "../view-mode-chrome";

interface GetCellDetailsSuccessProps {
  result?: {
    status?: string;
    message?: string;
    id?: string;
  };
  viewMode?: string;
}

export function GetCellDetailsSuccess({ result, viewMode}: GetCellDetailsSuccessProps) {
  const chrome = getViewModeChrome(viewMode);
  // Parse step count from message like "Retrieved details for 4 cell(s): [1, 2, 3, 4]"
  const stepCount = result?.message?.match(/(\d+) cell\(s\)/)?.[1] || "0";
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="w-full"
    >
      <div className={`rounded-md border ${chrome.card} overflow-hidden`}>
        <div className="px-3 py-2 flex items-center justify-between gap-3 min-w-0">
          <div className="flex items-center gap-2.5">
            <motion.div 
              className="flex items-center justify-center shrink-0 relative"
              initial={{ rotate: -180 }}
              animate={{ rotate: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <FileCode2
                className={`w-3.5 h-3.5 ${chrome.iconMuted} shrink-0`}
                strokeWidth={2}
              />
              <motion.div
                className="absolute inset-0 rounded bg-muted/30"
                initial={{ scale: 1, opacity: 0.5 }}
                animate={{ scale: 1.5, opacity: 0 }}
                transition={{ duration: 0.6 }}
              />
            </motion.div>

            <div className="flex flex-col">
              <span className={`text-[13px] font-semibold ${chrome.text} truncate`}>
                Analysis complete
              </span>
              <motion.span 
                className={`text-[11px] ${chrome.subtext} truncate`}
                initial={{ opacity: 0, x: -5 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 }}
              >
                {stepCount} {parseInt(stepCount) === 1 ? 'step' : 'steps'} reviewed
              </motion.span>
            </div>
          </div>

          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ 
              delay: 0.1, 
              type: "spring", 
              stiffness: 300,
              damping: 15
            }}
          >
            <CircleCheckBig className="h-4 w-4 text-green-500" />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
